// The mapped play icon: its size from publish data, and the `!important`
// styles kept on it while popover.js writes its own.
import type { ExperiencePublishData } from "@/types/experience";

type PublishData = ExperiencePublishData | null | undefined;

// Square fallback size used when the publish-data API doesn't return
// iconWidth / iconHeight (e.g. embed row missing on the experience).
export const PLAY_ICON_DEFAULT_SIZE = 85;

export function pickIconDims(publishData: PublishData): {
  width: number;
  height: number;
} {
  const data: ExperiencePublishData = publishData || {};
  const w = Number.parseInt(data.iconWidth as string, 10);
  const h = Number.parseInt(data.iconHeight as string, 10);
  const width = Number.isFinite(w) && w > 0 ? w : PLAY_ICON_DEFAULT_SIZE;
  const height = Number.isFinite(h) && h > 0 ? h : PLAY_ICON_DEFAULT_SIZE;
  return { width, height };
}

// Read the authoritative play-icon size off the element (stamped by
// `buildAnchor` from the publish-data API response).
export function readPlayIconDims(playIcon: HTMLElement | null): {
  width: number;
  height: number;
} {
  const w = Number.parseInt(playIcon?.dataset?.pixmotoIconWidth || "", 10);
  const h = Number.parseInt(playIcon?.dataset?.pixmotoIconHeight || "", 10);
  return {
    width: Number.isFinite(w) && w > 0 ? w : PLAY_ICON_DEFAULT_SIZE,
    height: Number.isFinite(h) && h > 0 ? h : PLAY_ICON_DEFAULT_SIZE,
  };
}

// Properties we keep authoritative on the play icon. Whenever
// popover.js mutates the icon's `style` attribute, we restore any
// of these whose value or `!important` priority got changed. The
// list is intentionally focused on positioning + sizing — color,
// border-radius, etc. are left alone so popover-driven hover
// swaps still work. Width/height come from the publish-data API
// (iconWidth / iconHeight on the embed) so the inline icon matches
// what popover.js eventually paints, avoiding a size-flash.
export function buildPlayIconImportantStyles(
  playIcon: HTMLElement,
): Record<string, string> {
  const dims = readPlayIconDims(playIcon);
  const w = `${dims.width}px`;
  const h = `${dims.height}px`;
  return {
    position: "absolute",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    margin: "auto",
    "border-style": "none",
    width: w,
    height: h,
    "max-width": w,
    "max-height": h,
    "min-width": "0",
    "min-height": "0",
    padding: "0",
    display: "block",
    visibility: "visible",
    "pointer-events": "none",
  };
}

export function applyPlayIconStyles(playIcon: HTMLElement | null) {
  if (!playIcon || !playIcon.style) {
    return;
  }
  const styles = buildPlayIconImportantStyles(playIcon);
  for (const prop in styles) {
    const want = styles[prop];
    if (
      playIcon.style.getPropertyValue(prop) !== want ||
      playIcon.style.getPropertyPriority(prop) !== "important"
    ) {
      playIcon.style.setProperty(prop, want, "important");
    }
  }
}

export function guardPlayIconStyles(playIcon: HTMLElement | null) {
  if (!playIcon || typeof MutationObserver !== "function") {
    applyPlayIconStyles(playIcon);
    return;
  }
  applyPlayIconStyles(playIcon);
  // Observer re-applies on every style mutation. The early-exit in
  // `applyPlayIconStyles` (only writes properties that diverge)
  // means our own writes don't trigger an infinite mutation loop —
  // after one corrective pass everything matches and the next
  // observer callback finds nothing to change.
  const observer = new MutationObserver(() => applyPlayIconStyles(playIcon));
  observer.observe(playIcon, {
    attributes: true,
    attributeFilter: ["style"],
  });
}

export function setImportantStyle(
  el: Element | null | undefined,
  prop: string,
  value: string,
) {
  if (
    !el ||
    !(el as HTMLElement).style ||
    typeof (el as HTMLElement).style.setProperty !== "function"
  ) {
    return;
  }
  (el as HTMLElement).style.setProperty(prop, value, "important");
}
